// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { color, xScreen } from "@stoneage/XDL/Utilities";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    imageUrl1 = "imageUrl1",
    title1 = "title1",
    copy1 = "copy1",
    linkText1 = "linkText1",
    url1 = "url1",
    openInNewTab1 = "openInNewTab1",
    imageAltText1 = "imageAltText1",
    imageUrl2 = "imageUrl2",
    title2 = "title2",
    copy2 = "copy2",
    linkText2 = "linkText2",
    url2 = "url2",
    openInNewTab2 = "openInNewTab2",
    imageAltText2 = "imageAltText2",
}

type infoCard = {
    imageUrl: string;
    title: string;
    copy: string;
    linkText: string;
    url: LinkFieldValue;
    openInNewTab: boolean;
    imageAltText: string;
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.imageUrl1]: string;
        [fields.title1]: string;
        [fields.copy1]: string;
        [fields.linkText1]: string;
        [fields.url1]: LinkFieldValue;
        [fields.openInNewTab1]: boolean;
        [fields.imageAltText1]: string;
        [fields.imageUrl2]: string;
        [fields.title2]: string;
        [fields.copy2]: string;
        [fields.linkText2]: string;
        [fields.url2]: LinkFieldValue;
        [fields.openInNewTab2]: boolean;
        [fields.imageAltText2]: string;
    };
}

type Props = OwnProps;
export interface HomepageInfoCardStyles {
    widgetContainer?: InjectableCss;
    infoCardsContainer?: InjectableCss;
    infoCard?: InjectableCss;
    infoCardBordered?: InjectableCss;
    image?: LazyImageProps;
    title?: TypographyProps;
    copy?: TypographyProps;
    link?: LinkProps;
}

const infoCardStyle = css`
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 215px;
    width: 418px;
    padding: 20px;
    background-color: ${color("common.ui10")};
    box-shadow: inset 0 -3px 0 0 ${color("primary.main")};
    ${xScreen("lte", "md")} {
        height: 237px;
        width: 360px;
    }
    ${xScreen("lte", "sm")} {
        height: 233px;
        width: 328px;
    }
`;

export const homepageInfoCardStyles: HomepageInfoCardStyles = {
    widgetContainer: {
        css: css`
            position: relative;
            z-index: 9;
        `,
    },
    infoCardsContainer: {
        css: css`
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            display: flex;
            ${xScreen("lte", "xs")} {
                position: static;
                transform: initial;
                justify-content: center;
                margin-top: -75px;
                margin-bottom: -75px;
                flex-direction: column;
                align-items: center;
            }
        `,
    },
    infoCard: {
        css: infoCardStyle,
    },
    infoCardBordered: {
        css: css`
            ${infoCardStyle};
            border-left: 1px solid ${color("common.black")};
        `,
    },
    image: {
        width: "35px",
        height: "29px",
        css: css`
            margin-bottom: 14px;
            object-fit: contain;
            overflow: hidden;
        `,
    },
    title: {
        variant: "h3",
        as: "h3",
        color: "common.white",
    },
    copy: {
        color: "common.ui5",
    },
    link: {
        color: "common.white",
        icon: {
            iconProps: {
                src: "ChevronRight",
                size: 15,
            },
            position: "right",
        },
    },
};

const styles = homepageInfoCardStyles;

const HomepageInfoCard = ({ id, fields }: Props) => {
    const InfoCard = ({ cardNum }: { cardNum: 1 | 2 }) => {
        const getField = (key: keyof infoCard) => fields[`${key}${cardNum}` as keyof typeof fields];

        const linkUrl = useGetLink(getField("url") as LinkFieldValue);
        // border is applied only for second card
        const getCardStyle = () => {
            if (cardNum === 1) {
                return styles.infoCard;
            }

            return styles.infoCardBordered;
        };

        return (
            <StyledWrapper className="homepage-info-card" {...getCardStyle()}>
                <StyledWrapper>
                    <LazyImage
                        src={getField("imageUrl") as string}
                        altText={getField("imageAltText") as string}
                        width="35"
                        height="29"
                        {...styles.image}
                    />
                </StyledWrapper>
                <Typography {...styles.title}>{getField("title")}</Typography>
                <Typography {...styles.copy}>{parse(getField("copy") as string, parserOptions)}</Typography>
                <Link
                    href={linkUrl.url}
                    target={(getField("openInNewTab") as boolean) ? "_blank" : "_self"}
                    {...styles.link}
                    id={`hp_button_hpInfoCard${cardNum}_${id}`}
                >
                    {getField("linkText")}
                </Link>
            </StyledWrapper>
        );
    };

    return (
        <StyledWrapper {...styles.widgetContainer}>
            <StyledWrapper {...styles.infoCardsContainer}>
                <InfoCard cardNum={1} />
                <InfoCard cardNum={2} />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: HomepageInfoCard,
    definition: {
        group: "XNGAGE" as any,
        fieldDefinitions: [
            {
                name: fields.imageUrl1,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.imageAltText1,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.title1,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.copy1,
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.linkText1,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                validate(value: string | null) {
                    if (!value) {
                        return null;
                    }

                    return value.length > 40 ? "Max character allowed is 40" : null;
                },
            },
            {
                name: fields.url1,
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
            },
            {
                name: fields.openInNewTab1,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
            },
            {
                name: fields.imageUrl2,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.imageAltText2,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.title2,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.copy2,
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.linkText2,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                validate(value: string | null) {
                    if (!value) {
                        return null;
                    }

                    return value.length > 40 ? "Max character allowed is 40" : null;
                },
            },
            {
                name: fields.url2,
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
            },
            {
                name: fields.openInNewTab2,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
            },
        ],
    },
};

export default widgetModule;
